import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { LuX } from "react-icons/lu";
import { Button, Dialog, RadioCards } from "../../../components/ui";
import { TIERS_PAGOS } from "../../../constants/paidPlans";
import { planoChaveLabel } from "../../../constants/billing";
import { PlanoChave } from "../../../types/billing.types";
import { formatDate, t } from "../../../i18n";
import { PATHS } from "../../../app/paths";

interface CancelSubscriptionDialogProps {
  planKey: PlanoChave;
  nextCharge?: string;
  /** Cancelamento no fim já agendado: só resta antecipar para agora. */
  onlyNow?: boolean;
  /** Assinatura no período de teste: cancelar não gera cobrança nenhuma. */
  inTrial?: boolean;
  loading: boolean;
  onConfirm: (now: boolean) => void;
  onClose: () => void;
}

type When = "end" | "now";

/** Cancelamento em duas etapas: retenção (o que o médico perde) e quando cancelar. */
export const CancelSubscriptionDialog: React.FC<CancelSubscriptionDialogProps> = ({
  planKey, nextCharge, onlyNow = false, inTrial = false, loading, onConfirm, onClose,
}) => {
  const navigate = useNavigate();
  const [step, setStep] = useState<"retention" | "confirm">(onlyNow ? "confirm" : "retention");
  const [when, setWhen] = useState<When>(onlyNow ? "now" : "end");

  const tier = TIERS_PAGOS.find((tierItem) => Object.values(tierItem.chaves).includes(planKey));
  const planName = tier?.name ?? planoChaveLabel(planKey);

  const seePlans = () => {
    onClose();
    navigate(PATHS.plans);
  };

  const atEndDescription = inTrial
    ? nextCharge ? t("settings.cancel.atEndTrialOn", { date: formatDate(nextCharge) }) : t("settings.cancel.atEndTrial")
    : nextCharge ? t("settings.cancel.atEndOn", { date: formatDate(nextCharge) }) : t("settings.cancel.atEndPaid");

  if (step === "retention") {
    return (
      <Dialog
        open
        size="sm"
        onClose={onClose}
        title={t("settings.cancel.retentionTitle", { plan: planName })}
        description={t("settings.cancel.retentionBody")}
        footer={
          <div className="flex w-full flex-col gap-2">
            <Button variant="primary" onClick={onClose}>{t("settings.cancel.keep")}</Button>
            <Button variant="text" onClick={() => setStep("confirm")}>{t("settings.cancel.continue")}</Button>
          </div>
        }
      >
        {tier && (
          <ul className="flex flex-col gap-2 rounded-card border border-primary-line bg-primary-tint p-4">
            {tier.features.map((feature) => (
              <li key={feature} className="flex items-center gap-2.5 text-body text-fg">
                <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-danger-soft text-danger" aria-hidden>
                  <LuX size={12} />
                </span>
                {feature}
              </li>
            ))}
          </ul>
        )}
        {tier?.id === "profissional" && (
          <p className="mt-3 text-center text-caption text-fg-muted">
            {t("settings.cancel.cheaper")}{" "}
            <button type="button" onClick={seePlans} className="font-semibold text-primary hover:underline">
              {t("settings.cancel.seePlans")}
            </button>
          </p>
        )}
      </Dialog>
    );
  }

  return (
    <Dialog
      open
      size="sm"
      onClose={onClose}
      closeDisabled={loading}
      title={onlyNow ? t("settings.cancel.nowTitle") : t("settings.cancel.howTitle")}
      description={onlyNow ? t("settings.cancel.nowBody") : t("settings.cancel.howBody")}
      footer={
        <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:[&>*]:flex-1">
          <Button variant="secondary" onClick={() => (onlyNow ? onClose() : setStep("retention"))} disabled={loading}>
            {t("settings.cancel.back")}
          </Button>
          <Button variant="danger" onClick={() => onConfirm(when === "now")} loading={loading}>
            {t("settings.cancel.confirm")}
          </Button>
        </div>
      }
    >
      <RadioCards<When>
        label={t("settings.cancel.when")}
        columns={1}
        value={when}
        onChange={setWhen}
        disabled={loading}
        options={[
          ...(onlyNow ? [] : [{ value: "end" as const, title: t("settings.cancel.atEnd"), badge: t("settings.cancel.recommended"), description: atEndDescription }]),
          { value: "now", title: t("settings.cancel.now"), description: inTrial ? t("settings.cancel.nowTrial") : t("settings.cancel.nowPaid") },
        ]}
      />
    </Dialog>
  );
};
